import { inject, Injectable, InjectionToken } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import {
  AlgorithmUsage,
  DeprecateAlgorithm,
  type IDeprecateAlgorithmHandler,
  type IListAlgorithmUsageHandler,
  type IUpdateAlgorithmMetadataHandler,
  type IUpdatePipelineAlgorithmHandler,
  ListAlgorithmUsage,
  UpdateAlgorithmMetadata,
  UpdatePipelineAlgorithm,
} from '@protocols/use-cases';
import { combineLatest, from, Observable, of, pipe } from 'rxjs';
import { catchError, concatMap, filter, finalize, map, mergeMap, reduce, switchMap, tap } from 'rxjs/operators';
import { ExecutionEnvironment, type PipelineRepository, PipelineStep } from '@telespot/domain';
import { AlgorithmData } from '../../../state/algorithm.state';
import { AlgorithmEffects as AlgorithmStore } from '../../../effects/algorithm.effects';
import { concatLatestFrom } from '@ngrx/operators';

export interface AlgorithmUsageData {
  data: AlgorithmUsage | undefined;
  error: string | undefined;
  loading: boolean;
}

export interface SaveEditorMetadataPayload {
  algorithmId: string;
  name: string;
  description: string;
  version: string;
  regulated: boolean;
}

interface AlgorithmsFacadeState {
  /**
   * Usage data for algorithms.
   */
  algorithmUsageData: EntityState<{
    algorithmId: string;
    usage: AlgorithmUsageData;
  }>;

  /**
   * Error state.
   */
  error: string | undefined;

  /**
   * Loading state.
   */
  loading: boolean;

  /**
   * Algorithm editor route: load/save state (separate from list usage).
   */
  editor: {
    algorithm: AlgorithmData | undefined;
    loading: boolean;
    saving: boolean;
    loadError: string | undefined;
    saveError: string | undefined;
    saveSuccess: number;
  };
}

export const ALGORITHM_FACADE_PIPELINE_REPOSITORY = new InjectionToken<PipelineRepository>(
  'ALGORITHM_FACADE_PIPELINE_REPOSITORY'
);
export const ALGORITHM_FACADE_LIST_ALGORITHM_USAGE_HANDLER = new InjectionToken<IListAlgorithmUsageHandler>(
  'ALGORITHM_FACADE_LIST_ALGORITHM_USAGE_HANDLER'
);
export const ALGORITHM_FACADE_UPDATE_PIPELINE_ALGORITHM_HANDLER = new InjectionToken<IUpdatePipelineAlgorithmHandler>(
  'ALGORITHM_FACADE_UPDATE_PIPELINE_ALGORITHM_HANDLER'
);
export const ALGORITHM_FACADE_DEPRECATE_ALGORITHM_HANDLER = new InjectionToken<IDeprecateAlgorithmHandler>(
  'ALGORITHM_FACADE_DEPRECATE_ALGORITHM_HANDLER'
);
export const ALGORITHM_FACADE_UPDATE_ALGORITHM_METADATA_HANDLER = new InjectionToken<IUpdateAlgorithmMetadataHandler>(
  'ALGORITHM_FACADE_UPDATE_ALGORITHM_METADATA_HANDLER'
);

@Injectable()
export class AlgorithmsFacade extends ComponentStore<AlgorithmsFacadeState> {
  public readonly algorithmStore = inject(AlgorithmStore);
  private readonly pipelineRepository = inject<PipelineRepository>(ALGORITHM_FACADE_PIPELINE_REPOSITORY);
  private readonly listAlgorithmUsageHandler = inject<IListAlgorithmUsageHandler>(ALGORITHM_FACADE_LIST_ALGORITHM_USAGE_HANDLER);
  private readonly updatePipelineAlgorithmHandler = inject<IUpdatePipelineAlgorithmHandler>(ALGORITHM_FACADE_UPDATE_PIPELINE_ALGORITHM_HANDLER);
  private readonly deprecateAlgorithmHandler = inject<IDeprecateAlgorithmHandler>(ALGORITHM_FACADE_DEPRECATE_ALGORITHM_HANDLER);
  private readonly updateAlgorithmMetadataHandler = inject<IUpdateAlgorithmMetadataHandler>(ALGORITHM_FACADE_UPDATE_ALGORITHM_METADATA_HANDLER);

  // Entity adapter
  private readonly algorithmUsageAdapter: EntityAdapter<{
    algorithmId: string;
    usage: AlgorithmUsageData;
  }>;

  constructor() {
    const tempAdapter = createEntityAdapter<{
      algorithmId: string;
      usage: AlgorithmUsageData;
    }>({
      selectId: (item) => item.algorithmId,
    });
    super({
      algorithmUsageData: tempAdapter.getInitialState(),
      error: undefined,
      loading: false,
      editor: {
        algorithm: undefined,
        loading: false,
        saving: false,
        loadError: undefined,
        saveError: undefined,
        saveSuccess: 0,
      },
    });

    this.algorithmUsageAdapter = tempAdapter;
  }

  public readonly facadeLoading$ = this.select((state) => state.loading);

  public readonly algorithmUsageData$ = this.select((state) =>
    this.algorithmUsageAdapter.getSelectors().selectAll(state.algorithmUsageData)
  );

  public readonly algorithmUsageMap$ = this.algorithmUsageData$.pipe(
    map((usageData) => {
      const usages: Record<string, AlgorithmUsageData> = {};
      usageData.forEach((item) => {
        usages[item.algorithmId] = item.usage;
      });
      return usages;
    })
  );

  // Delegated selectors from AlgorithmStore
  public readonly algorithms$ = this.algorithmStore.items$;
  public readonly totalAlgorithms$ = this.algorithmStore.totalCount$;
  public readonly emptyList$ = this.algorithmStore.noItems$;
  public readonly error$ = this.select((state) => state.error);

  public readonly editorAlgorithm$ = this.select((state) => state.editor.algorithm);
  public readonly editorLoading$ = this.select((state) => state.editor.loading);
  public readonly editorSaving$ = this.select((state) => state.editor.saving);
  public readonly editorLoadError$ = this.select((state) => state.editor.loadError);
  public readonly editorSaveError$ = this.select((state) => state.editor.saveError);
  public readonly editorSaveSuccess$ = this.select((state) => state.editor.saveSuccess);
  public readonly editorBusy$ = this.select((state) => state.editor.loading || state.editor.saving);

  public readonly loadEditorAlgorithm = this.effect((algorithmId$: Observable<string>) =>
    algorithmId$.pipe(
      tap(() =>
        this.patchState((state) => ({
          editor: {
            ...state.editor,
            loading: true,
            loadError: undefined,
          },
        }))
      ),
      switchMap((algorithmId) =>
        from(this.algorithmStore.loadAlgorithm(algorithmId)).pipe(
          tap((algorithm) =>
            this.patchState((state) => ({
              editor: {
                ...state.editor,
                algorithm,
                loading: false,
                loadError: undefined,
              },
            }))
          ),
          catchError((err: unknown) => {
            const message = err instanceof Error ? err.message : 'Failed to load algorithm';
            this.patchState((state) => ({
              editor: {
                ...state.editor,
                algorithm: undefined,
                loading: false,
                loadError: message,
              },
            }));
            return of(null);
          })
        )
      )
    )
  );

  public readonly saveEditorMetadata = this.effect((payload$: Observable<SaveEditorMetadataPayload>) =>
    payload$.pipe(
      tap(() =>
        this.patchState((state) => ({
          editor: { ...state.editor, saving: true, saveError: undefined },
        }))
      ),
      switchMap((payload) =>
        from(
          this.updateAlgorithmMetadataHandler.execute(
            new UpdateAlgorithmMetadata({
              algorithmId: payload.algorithmId,
              name: payload.name,
              description: payload.description,
              version: payload.version,
              regulated: payload.regulated,
            })
          )
        ).pipe(
          tap(() => {
            this.algorithmStore.reloadSingleAlgorithm(payload.algorithmId);
            this.patchState((state) => ({
              editor: {
                ...state.editor,
                saving: false,
                saveError: undefined,
                saveSuccess: state.editor.saveSuccess + 1,
              },
            }));
          }),
          catchError((err: unknown) => {
            const message = err instanceof Error ? err.message : 'Failed to update algorithm';
            this.patchState((state) => ({
              editor: { ...state.editor, saving: false, saveError: message },
            }));
            return of(null);
          })
        )
      )
    )
  );

  // Combined selectors from AlgorithmStore and Facade
  public readonly loadingAlgorithms$ = combineLatest([this.algorithmStore.loading$, this.facadeLoading$]).pipe(
    map(([storeLoading, facadeLoading]) => storeLoading || facadeLoading)
  );

  // Delegated updaters from AlgorithmStore
  public readonly setIncludeDeprecated = this.algorithmStore.setIncludeDeprecated;
  public readonly setEnvironmentFilter = this.algorithmStore.setEnvironmentFilter;

  private readonly setError = this.updater((state, error: string | undefined) => ({ ...state, error }));
  private readonly setLoading = this.updater((state, loading: boolean) => ({ ...state, loading }));

  private readonly saveAlgorithmUsage = this.updater(
    (state, { algorithmId, usage }: { algorithmId: string; usage: AlgorithmUsage }) => {
      return {
        ...state,
        loading: false,
        algorithmUsageData: this.algorithmUsageAdapter.upsertOne(
          { algorithmId: algorithmId, usage: { data: usage, error: undefined, loading: false } },
          state.algorithmUsageData
        ),
      };
    }
  );

  private readonly saveAlgorithmUsageError = this.updater(
    (state, { algorithmId, error }: { algorithmId: string; error: string }) => {
      const previousData = state.algorithmUsageData.entities[algorithmId]?.usage?.data;
      return {
        ...state,
        loading: false,
        algorithmUsageData: this.algorithmUsageAdapter.upsertOne(
          { algorithmId: algorithmId, usage: { data: previousData, error: error, loading: false } },
          state.algorithmUsageData
        ),
      };
    }
  );

  private readonly clearAlgorithmUsage = this.updater((state, algorithmId: string) => ({
    ...state,
    algorithmUsageData: this.algorithmUsageAdapter.removeOne(algorithmId, state.algorithmUsageData),
  }));

  private readonly setAlgorithmUsageLoading = this.updater(
    (state, { algorithmId, loading }: { algorithmId: string; loading: boolean }) => {
      const previousUsage = state.algorithmUsageData.entities[algorithmId]?.usage ?? {
        data: undefined,
        error: undefined,
        loading: loading,
      };

      return {
        ...state,
        algorithmUsageData: this.algorithmUsageAdapter.upsertOne(
          {
            algorithmId,
            usage: { ...previousUsage, loading },
          },
          state.algorithmUsageData
        ),
      };
    }
  );

  public readonly listAlgorithmUsage = this.effect((algorithmId$: Observable<string>) =>
    algorithmId$.pipe(
      mergeMap(
        (algorithmId) =>
          from(this.listAlgorithmUsageHandler.execute(new ListAlgorithmUsage({ algorithmId, batchSize: 5 }))).pipe(
            tap((usage: AlgorithmUsage) => this.saveAlgorithmUsage({ algorithmId, usage })),
            catchError((): Observable<AlgorithmUsage | null> => {
              this.saveAlgorithmUsageError({ algorithmId, error: 'table.algorithms.error.list' });
              return of(null);
            })
          ),
        2 // max concurrent operations
      )
    )
  );

  public readonly updatePipelineStepExecutor = this.effect(
    (action$: Observable<{ pipelineStepId: string; executorId: string | undefined; oldExecutorId: string }>) =>
      action$.pipe(
        filter(({ oldExecutorId, executorId }) => oldExecutorId !== executorId),
        tap(({ oldExecutorId, executorId }) => this.setLoadingForAffectedAlgorithms(oldExecutorId, executorId)),
        concatMap(({ oldExecutorId, executorId, pipelineStepId }) =>
          this.checkAlgorithmDeprecationIfNeeded(executorId).pipe(
            map(() => ({ pipelineStepId, executorId, oldExecutorId }))
          )
        ),
        switchMap(({ pipelineStepId, executorId, oldExecutorId }) =>
          this.executeUpdatePipelineAlgorithm(pipelineStepId, executorId, oldExecutorId)
        )
      )
  );

  private setLoadingForAffectedAlgorithms(oldExecutorId: string, executorId: string | undefined): void {
    this.setAlgorithmUsageLoading({ algorithmId: oldExecutorId, loading: true });
    if (executorId) {
      this.setAlgorithmUsageLoading({ algorithmId: executorId, loading: true });
    }
  }

  private checkAlgorithmDeprecationIfNeeded(executorId: string | undefined): Observable<void> {
    if (!executorId) {
      return of(undefined);
    }

    return from(this.algorithmStore.loadAlgorithm(executorId)).pipe(
      tap((algorithm) => {
        if (algorithm.deprecated) {
          this.changeAlgorithmDeprecationStatus(algorithm);
        }
      }),
      map(() => undefined)
    );
  }

  private executeUpdatePipelineAlgorithm(
    pipelineStepId: string,
    executorId: string | undefined,
    oldExecutorId: string
  ): Observable<unknown> {
    return from(
      this.updatePipelineAlgorithmHandler.execute(
        new UpdatePipelineAlgorithm({ pipelineStepId, algorithmId: executorId })
      )
    ).pipe(
      tap(() => this.relistAlgorithmUsageForBothExecutors(oldExecutorId, executorId)),
      catchError((error) => this.handleUpdatePipelineError(error, oldExecutorId, executorId))
    );
  }

  private relistAlgorithmUsageForBothExecutors(oldExecutorId: string, executorId: string | undefined): void {
    this.reloadAndRelist(oldExecutorId);
    if (executorId) {
      this.reloadAndRelist(executorId);
    }
  }

  private handleUpdatePipelineError(
    error: unknown,
    oldExecutorId: string,
    executorId: string | undefined
  ): Observable<unknown> {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    this.setError(errorObj.message);

    this.setAlgorithmUsageLoading({ algorithmId: oldExecutorId, loading: false });
    if (executorId) {
      this.setAlgorithmUsageLoading({ algorithmId: executorId, loading: false });
    }

    return of(null);
  }

  private readonly loadPipelineSteps = (algorithmId: string) =>
    from(this.pipelineRepository.findStepsForAlgorithm(algorithmId)).pipe(map((steps) => ({ algorithmId, steps })));

  private readonly handleRemoveFromPipelineError = (algorithmId: string) => {
    this.saveAlgorithmUsageError({
      algorithmId,
      error: 'table.algorithms.error.deprecate.remove_from_pipeline',
    });
    return of(false);
  };

  private readonly removeAlgorithmFromPipelineStep = (algorithmId: string, step: PipelineStep) =>
    from(
      this.updatePipelineAlgorithmHandler.execute(
        new UpdatePipelineAlgorithm({ pipelineStepId: step.id, algorithmId: undefined })
      )
    ).pipe(
      map(() => true),
      catchError(() => this.handleRemoveFromPipelineError(algorithmId))
    );

  private readonly removeAlgorithmFromPipelines = (algorithmId: string, steps: PipelineStep[]) =>
    from(steps).pipe(
      mergeMap((step) => this.removeAlgorithmFromPipelineStep(algorithmId, step), 3),
      reduce((allSucceeded, stepSucceeded) => allSucceeded && stepSucceeded, true)
    );

  private readonly deprecateAlgorithm = this.effect((algorithmId$: Observable<string>) =>
    algorithmId$.pipe(
      tap(() => this.setLoading(true)),
      switchMap((algorithmId) => this.loadPipelineSteps(algorithmId)),
      switchMap(({ algorithmId, steps }) => {
        return this.removeAlgorithmFromPipelines(algorithmId, steps).pipe(
          switchMap((allSucceeded) => {
            if (!allSucceeded) {
              this.saveAlgorithmUsageError({
                algorithmId,
                error: 'table.algorithms.error.deprecate.remove_from_pipeline',
              });
              return of(null);
            }

            return from(
              this.deprecateAlgorithmHandler.execute(new DeprecateAlgorithm({ algorithmId, deprecate: true }))
            ).pipe(this.cleanupDeprecated(algorithmId));
          }),
          catchError(() => {
            this.saveAlgorithmUsageError({ algorithmId, error: 'table.algorithms.error.deprecate.operation_failed' });
            return of(null);
          })
        );
      })
    )
  );

  private readonly undeprecateAlgorithm = this.effect((algorithmId$: Observable<string>) =>
    algorithmId$.pipe(
      switchMap((algorithmId) =>
        from(this.deprecateAlgorithmHandler.execute(new DeprecateAlgorithm({ algorithmId, deprecate: false }))).pipe(
          concatLatestFrom(() => [this.algorithmStore.includeDeprecated$]),
          tap(([, includeDeprecated]) => {
            if (includeDeprecated) {
              this.reloadAndRelist(algorithmId);
            } else {
              this.algorithmStore.relistPage();
            }
          }),
          catchError(() => {
            this.saveAlgorithmUsageError({ algorithmId, error: 'table.algorithms.error.undeprecate' });
            return of(null);
          })
        )
      )
    )
  );

  public readonly changeAlgorithmDeprecationStatus = this.effect((algorithm$: Observable<AlgorithmData>) =>
    algorithm$.pipe(
      tap((algorithm: AlgorithmData) => {
        if (algorithm.deprecated) {
          this.undeprecateAlgorithm(algorithm.id);
        } else {
          this.deprecateAlgorithm(algorithm.id);
        }
      })
    )
  );

  // Delegated effects from AlgorithmStore
  public readonly initializeAlgorithms = this.algorithmStore.initializeItems;
  public readonly changePageSize = this.algorithmStore.changePageSize;
  public readonly loadMoreAlgorithms = this.algorithmStore.loadMoreItems;

  // Helper methods
  private readonly cleanupDeprecated = (algorithmId: string) =>
    pipe(
      concatLatestFrom(() => this.algorithmStore.includeDeprecated$),
      tap(([, includeDeprecated]) => {
        if (includeDeprecated) {
          this.reloadAndRelist(algorithmId);
        } else {
          this.algorithmStore.removeItem(algorithmId);
          this.clearAlgorithmUsage(algorithmId);
        }
      }),
      finalize(() => this.setLoading(false))
    );

  private readonly reloadAndRelist = (algorithmId: string) => {
    this.algorithmStore.reloadSingleAlgorithm(algorithmId);
    this.listAlgorithmUsage(algorithmId);
  };

  public readonly getPipelineData = (algorithmId: string, pipelineStepId: string) => {
    return this.get((state) => {
      const pipelines = state.algorithmUsageData.entities[algorithmId]?.usage.data?.pipelines;
      const pipeline = Object.values(pipelines).find((pipeline) => pipeline.stepId === pipelineStepId);
      return {
        id: pipeline?.id,
        name: pipeline?.name,
        description: pipeline?.description,
        organizations: pipeline?.organizations,
        environment: pipeline?.env === 'cloud' ? ExecutionEnvironment.CLOUD : ExecutionEnvironment.MOBILE,
      };
    });
  };

  public readonly getCombinedPipelineData = (algorithmId: string) => {
    return this.get((state) => {
      const pipelines = state.algorithmUsageData.entities[algorithmId]?.usage.data?.pipelines;

      if (!pipelines) {
        return undefined;
      }

      // Build a combined OrganizationStructure from all pipelines
      const combinedOrganizations: Record<
        string,
        {
          id: string;
          name: string | { [key: string]: string };
          workspaces: Record<string, { id: string; name: string | { [key: string]: string } }>;
        }
      > = {};

      for (const pipeline of Object.values(pipelines)) {
        for (const [orgId, organization] of Object.entries(pipeline.organizations)) {
          if (!combinedOrganizations[orgId]) {
            // First time seeing this organization, initialize it with a copy of its workspaces
            combinedOrganizations[orgId] = {
              id: organization.id,
              name: organization.name,
              workspaces: { ...organization.workspaces },
            };
          } else {
            // Organization already exists, merge the workspaces
            for (const [workspaceId, workspace] of Object.entries(organization.workspaces)) {
              if (!combinedOrganizations[orgId].workspaces[workspaceId]) {
                combinedOrganizations[orgId].workspaces[workspaceId] = workspace;
              }
            }
          }
        }
      }

      return combinedOrganizations;
    });
  };
}
